import React, { useState, useEffect, useMemo} from 'react';
import { Button, Modal } from 'react-bootstrap';
import { useSummaryByYear, useSummaryByMonth, useSummaryTransactions } from 'hooks/transactions.hook';
import { PieChart, Pie, Cell } from 'recharts';
import { currency, date } from 'helpers/formatter';
import { TableBasicClick } from 'components';
import * as Icon from 'react-bootstrap-icons';
import { TransactionReport } from './TransactionReport';
import { fetchAllTransactionsForAccounts } from 'helpers/api';
import { answerTransactionQuestion } from 'helpers/transaction-question';

export { Dashboard };

function Dashboard({ accounts, accountsIsLoading }) {
  
  const [title, setTitle] = useState("Dashboard");
  const [question, setQuestion] = useState('');
  const [questionResult, setQuestionResult] = useState(null);
  const [submittedQuestion, setSubmittedQuestion] = useState('');
  const [questionError, setQuestionError] = useState('');
  const [isAsking, setIsAsking] = useState(false);
  const [showYears, setShowYears] = useState(true);
  const [showTransactions, setShowTransactions] = useState(false);
  const [showMonths, setShowMonths] = useState(false);
  const [selectedYear, setSelectedYear] = useState(0);
  const [selectedMonth, setSelectedMonth] = useState(0);
  const [showReportDialog, setShowReportDialog] = useState(false);
  const [report, setReport] = useState({});

  const handleReportClose = () => setShowReportDialog(false);

  const { data: yearData, isLoading: isYearLoading } = useSummaryByYear();
  const [year, setYear] = useState([]);
  useEffect(() => { setYear(yearData); }, [yearData]);

  const { data: monthData, isLoading: isMonthLoading } = useSummaryByMonth(selectedYear);
  const [month, setMonth] = useState([]);
  useEffect(() => { setMonth(monthData); }, [monthData]);

  const { data: transactionsData, isLoading: isTransactionsLoading } = useSummaryTransactions(selectedYear, selectedMonth);
  const [transactions, setTransactions] = useState([]);
  useEffect(() => { setTransactions(transactionsData); }, [transactionsData]);

  const COLORS = ['#223e7d', '#b91946'];
  const RADIAN = Math.PI / 180;
  const renderCustomizedLabel = ({ cx, cy, midAngle, innerRadius, outerRadius, percent, index }) => {
    const radius = innerRadius + (outerRadius - innerRadius) * 0.5;
    const x = cx + radius * Math.cos(-midAngle * RADIAN);
    const y = cy + radius * Math.sin(-midAngle * RADIAN);
    return (
      <text x={x} y={y} fill="white" textAnchor={x > cx ? 'start' : 'end'} dominantBaseline="central">
        {`${(percent * 100).toFixed(0)}%`}
      </text>
    );
  };

  const handleShowMonths = (year) => {
    setShowYears(false);
    setShowTransactions(false);
    setShowMonths(true);
    setSelectedYear(year);
    setTitle(year);
  }
  const handleShowTransactions = (year, month, monthName) => {
    setShowMonths(false);
    setShowTransactions(true);
    setSelectedMonth(month);
    setTitle(`${monthName} ${year}`);
  }
  const handleShowYears = () => {
    setShowYears(true);
    setShowMonths(false);
    setTitle("Dashboard");
  }

  const handleChart = (e) => {
    setReport(e);
    setShowReportDialog(true);
  }

  const handleAskQuestion = async (event) => {
    event.preventDefault();
    const trimmedQuestion = question.trim();
    if (!trimmedQuestion || isAsking || accountsIsLoading) return;
    if (!accounts.length) {
      setQuestionError('No accounts are available to search.');
      return;
    }

    setQuestionError('');
    setQuestionResult(null);
    setSubmittedQuestion(trimmedQuestion);
    setIsAsking(true);

    try {
      const allTransactions = await fetchAllTransactionsForAccounts(accounts);
      setQuestionResult(answerTransactionQuestion(allTransactions, trimmedQuestion));
    } catch (error) {
      setQuestionError(error.message || 'Unable to search transactions. Please try again.');
    } finally {
      setIsAsking(false);
    }
  };

  const formatTrProps = (state = {}) => {
    //return { onClick: () => editOnClick(state.original) };
  };
  const columns = useMemo(
    () => [
      {
        Header: "Date",
        accessor: "date",
        Cell: ({ row }) => (
          date(row.original.date)
        )
      },
      {
        Header: "Payee",
        accessor: "payee",
      },
      {
        Header: "Category",
        accessor: "category",
      },
      {
        Header: "Tags",
        accessor: "tags",
      },
            {
        Header: "Withdrawal",
        accessor: "withdrawal",
        Cell: ({ row }) => (
          <div className='text-right'>{row.original.withdrawal !== 0 ? currency(-1 * row.original.withdrawal) : ""}</div>
        )
      },
      {
        Header: "Deposit",
        accessor: "deposit",
        Cell: ({ row }) => (
          <div className='text-right'>{row.original.deposit !== 0 ? currency(row.original.deposit) : ""}</div>
        )
      },
      {
        Header: "Account",
        accessor: "accountName",
      },
      {
        Header: "Bank",
        accessor: "bankName",
      },
    ],
    []
  );

  return (
    <div>
      <h1><Icon.BarChartFill size={30}/> {title}</h1>
      <p></p>
      <hr/>
      {showYears && (
        <section className="mb-4" aria-labelledby="transaction-question-title">
          <h2 id="transaction-question-title" className="h4">Ask about your transactions</h2>
          <p>Ask about payees, categories, dates, income, expenses, or amount thresholds.</p>
          <form onSubmit={handleAskQuestion}>
            <div className="input-group">
              <input
                aria-label="Question about transactions"
                className="form-control"
                type="text"
                value={question}
                onChange={(event) => setQuestion(event.target.value)}
                placeholder="e.g. How much did I spend at Costco last month?"
                maxLength={300}
              />
              <button className="btn btn-primary" type="submit" disabled={isAsking || accountsIsLoading || !question.trim()}>
                <Icon.Search size={18} /> {isAsking ? 'Searching...' : 'Ask'}
              </button>
            </div>
          </form>
          {isAsking && <div className="text-center mt-3" role="status"><div className="spinner-border" /> Loading transactions...</div>}
          {questionError && <div className="alert alert-danger mt-3" role="alert">{questionError}</div>}
          {questionResult && (
            <div className="mt-3">
              <h3 className="h5">Results for “{submittedQuestion}”</h3>
              <p>
                {questionResult.isLimited
                  ? `Showing the top ${questionResult.transactions.length} of ${questionResult.count} matching transactions. Totals cover all matching transactions.`
                  : `${questionResult.count} matching transaction${questionResult.count === 1 ? '' : 's'}.`}
                {' '}Income: ${currency(questionResult.income)}
                {' '}| Expenses: ${currency(questionResult.expenses)}
                {' '}| Net: ${currency(questionResult.net)}
              </p>
              {questionResult.count > 0
                ? <TableBasicClick responsive columns={columns} data={questionResult.transactions} formatRowProps={(state) => formatTrProps(state)} />
                : <p>No transactions matched that question.</p>}
            </div>
          )}
        </section>
      )}
      {showMonths && (
        <div className="menu-content">
          <p></p>
          <button className='btn btn-primary' onClick={() => handleShowYears()}><Icon.CaretLeftFill size={20}/> Back</button>&nbsp;
        </div>
      )}
      {showTransactions && (
        <div className="menu-content">
          <p></p>
          <button className='btn btn-primary' onClick={() => handleShowMonths(selectedYear)}><Icon.CaretLeftFill size={20}/> Back</button>&nbsp;
        </div>
      )}
      <p>&nbsp;</p>
        {showYears && year && year.map(y => (
          <div key={y.year} className='charts charts-hover' >
            <div className='chart-header' onClick={() => handleShowMonths(y.year)}>
              <button className='btn btn-primary'><Icon.Calendar3 size={15}/>  {y.year}</button>
            </div>
            <PieChart width={350} height={400} onClick={() => handleChart(y)}>
              <Pie
                data={y.chartData}
                cx="50%"
                cy="50%"
                labelLine={false}
                label={renderCustomizedLabel}
                outerRadius={100}
                fill="#8884d8"
                dataKey="value"
              >
                {y.chartData.map((entry, index) => (
                  <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                ))}
              </Pie>
            </PieChart>
            <div className='chart-footer'>
              <div className='chart-footer-content'>
                <p><span className='chart-income'></span><span className='chart-subtitle'>Income: <span>{currency(y.income)}</span></span></p>
                <p><span className='chart-expenses'></span><span className='chart-subtitle'>Expenses: <span>{currency(y.expenses)}</span></span></p>
                <p><span className='chart-gross'></span><span className='chart-subtitle'>Gross: <span className={y.gross < 0 ? 'chart-gross-loss' : 'chart-gross-gain'}>{currency(y.gross)}</span></span></p>
              </div>
            </div>
            <hr></hr>
          </div>
        ))}
      {isYearLoading && <div className='text-center'><div className="spinner-border spinner-border-lg"></div></div> }

      {showMonths && month && month.map(m => (
        <div key={m.month} className='charts charts-hover'>
          <div className='chart-header' onClick={() => handleShowTransactions(m.year, m.month, m.monthName)}>
            <button className='btn btn-primary'><Icon.Calendar3 size={15}/> {m.monthName} {m.year}</button>
          </div>
          <PieChart width={350} height={400} onClick={() => handleChart(m)}>
            <Pie
              data={m.chartData}
              cx="50%"
              cy="50%"
              labelLine={false}
              label={renderCustomizedLabel}
              outerRadius={100}
              fill="#8884d8"
              dataKey="value"
            >
              {m.chartData.map((entry, index) => (
                <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
              ))}
            </Pie>
          </PieChart>
          <div className='chart-footer'>
            <div className='chart-footer-content'>
              <p><span className='chart-income'></span><span className='chart-subtitle'>Income: <span>{currency(m.income)}</span></span></p>
              <p><span className='chart-expenses'></span><span className='chart-subtitle'>Expenses: <span>{currency(m.expenses)}</span></span></p>
              <p><span className='chart-gross'></span><span className='chart-subtitle'>Gross: <span className={m.gross < 0 ? 'chart-gross-loss' : 'chart-gross-gain'}>{currency(m.gross)}</span></span></p>
            </div>
          </div>
          <hr></hr>
        </div>
      ))}
      {isMonthLoading && <div className='text-center'><div className="spinner-border spinner-border-lg"></div></div> }

      {showTransactions && transactions && <TableBasicClick responsive columns={columns} data={transactions} formatRowProps={(state) => formatTrProps(state)} />}
      {isTransactionsLoading && <div className='text-center'><div className="spinner-border spinner-border-lg"></div></div> }

      <Modal
        size="xl"
        show={showReportDialog}
        onHide={() => setShowReportDialog(false)}
        aria-labelledby="modal-summary"
      >
        <Modal.Header closeButton closeVariant='white'>
          <Modal.Title id="modal-summary">
            {report.monthName ? 
            <>{`${report.year} ${report.monthName}`}</> : 
            <>{`${report.year}`}</>}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
            <TransactionReport accountId={0} report={report} />
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" type="button" onClick={handleReportClose}>Close</Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
}